import { useMemo } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { match } from 'ts-pattern';
import { useIxfAccountingV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-account-query-service-account-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import PinIcon from '@interxfi/design-system/icons/react/icon-pin';
import PowerIcon from '@interxfi/design-system/icons/react/icon-power';
import { IxfAccountingV1AccountStatus } from '@interxfi/types/generated/api';
import { ActivateChartOfAccountsAccountDialog } from '../../../../../../../../../../../features/accounting/chart-of-accounts/activate-chart-of-accounts-account-dialog';
import { ChartOfAccountsAccountEventDrawer } from '../../../../../../../../../../../features/accounting/chart-of-accounts/chart-of-accounts-account-event';
import { ChartOfAccountsAccountSetCodesDrawer } from '../../../../../../../../../../../features/accounting/chart-of-accounts/chart-of-accounts-account-set-codes-drawer';
import { ChartOfAccountsAccountStatusPill } from '../../../../../../../../../../../features/accounting/chart-of-accounts/chart-of-accounts-account-status-pill';
import { ChartOfAccountsClassificationPill } from '../../../../../../../../../../../features/accounting/chart-of-accounts/chart-of-accounts-classification-pill';
import { DeactivateChartOfAccountsAccountDialog } from '../../../../../../../../../../../features/accounting/chart-of-accounts/deactivate-chart-of-accounts-account-dialog';
import { UpdateChartOfAccountsAccountDrawer } from '../../../../../../../../../../../features/accounting/chart-of-accounts/update-chart-of-accounts-account-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/finance/_finance/accounting/account/_account/$accountId/_layout',
)({
  component: CoaAccountLayout,
  head: () => getPageHead('COA Account'),
});

function getMenuItems(accountId: string, status: IxfAccountingV1AccountStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: { Component: ChartOfAccountsAccountEventDrawer, props: { request: { accountId } } },
    },
    actions: match(status)
      .returnType<EntityActionMenuItem[]>()
      .with(IxfAccountingV1AccountStatus.Inactive, () => [
        {
          id: 'activate',
          icon: <PowerIcon aria-hidden width={16} />,
          label: 'Activate',
          interaction: {
            type: 'modal',
            modal: { Component: ActivateChartOfAccountsAccountDialog, props: { accountId } },
          },
          requiresWrite: true,
        },
      ])
      .with(IxfAccountingV1AccountStatus.Active, () => [
        {
          id: 'edit',
          icon: <EditIcon aria-hidden />,
          label: 'Edit',
          interaction: {
            type: 'drawer',
            drawer: { Component: UpdateChartOfAccountsAccountDrawer, props: { accountId } },
          },
          requiresWrite: true,
        } satisfies EntityActionMenuItem,
        {
          id: 'setCodes',
          icon: <PinIcon aria-hidden />,
          label: 'Set Codes',
          interaction: {
            type: 'drawer',
            drawer: { Component: ChartOfAccountsAccountSetCodesDrawer, props: { accountId } },
          },
          requiresWrite: true,
        } satisfies EntityActionMenuItem,
        {
          id: 'deactivate',
          icon: <PowerIcon aria-hidden width={16} />,
          label: 'Deactivate',
          interaction: {
            type: 'modal',
            modal: { Component: DeactivateChartOfAccountsAccountDialog, props: { accountId } },
          },
          requiresWrite: true,
        },
      ])
      .otherwise(() => []),
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load account', 'view this account');

function CoaAccountLayout() {
  const { accountId } = Route.useParams();
  const request = { accountId } as const;
  const { data, error, isPending } = useIxfAccountingV1AccountQueryServiceAccountGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const menuItems = useMemo(() => getMenuItems(accountId, data?.account?.status), [accountId, data?.account?.status]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <ChartOfAccountsAccountStatusPill isPending={showPending} size="large" status={data?.account?.status} />
            <ChartOfAccountsClassificationPill classification={data?.account?.data?.classification} isPending={showPending} size="large" />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <Outlet />
    </PageLayout>
  );
}
